import { useState, useEffect } from 'react';
import { type Icon, IconChevronRight, IconPlus } from '@tabler/icons-react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { fetchBlogs, type Blog } from '@/admin/lib/api';

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from '@/admin/components/ui/sidebar';

type NavItemProps = {
  title: string;
  url: string;
  icon?: Icon;
  items?: {
    title: string;
    url: string;
  }[];
  isBlogList?: boolean;
};

export function NavMainItem({ item }: { item: NavItemProps }) {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loadingBlogs, setLoadingBlogs] = useState(false);

  useEffect(() => {
    if (item.isBlogList && isExpanded) {
      const loadBlogs = async () => {
        try {
          setLoadingBlogs(true);
          const data = await fetchBlogs(10, 0); // Load top 10 recent blogs for sidebar
          setBlogs(data.blogs || []);
        } catch (err) {
          console.error('Failed to load blogs for sidebar:', err);
        } finally {
          setLoadingBlogs(false);
        }
      };
      loadBlogs();
    }
  }, [item.isBlogList, isExpanded]);

  const isExternalLink = (url: string) => {
    return url.startsWith('http://') || url.startsWith('https://');
  };

  const external = isExternalLink(item.url);
  const isActive =
    !external &&
    (item.url === '/'
      ? location.pathname === '/'
      : location.pathname === item.url ||
        location.pathname.startsWith(`${item.url}/`));
  const hasSubItems = (item.items && item.items.length > 0) || item.isBlogList;

  if (hasSubItems) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip={item.title}
          onClick={() => setIsExpanded(!isExpanded)}
          isActive={isActive && !isExpanded}
        >
          {item.icon && <item.icon />}
          <span className="flex-1">{item.title}</span>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
          >
            <IconChevronRight className="size-4" />
          </motion.div>
        </SidebarMenuButton>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
              className="overflow-hidden"
            >
              <SidebarMenuSub>
                {item.isBlogList ? (
                  <>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild>
                        <Link
                          to="/blogs"
                          className="text-primary font-medium flex items-center justify-between"
                        >
                          <span>All Blogs</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton asChild>
                        <Link
                          to="/blogs?new=true"
                          className="text-muted-foreground flex items-center gap-1.5"
                        >
                          <IconPlus className="size-3.5" />
                          <span>New Blog</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>

                    {loadingBlogs ? (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">
                        Loading...
                      </div>
                    ) : (
                      blogs.map(blog => (
                        <SidebarMenuSubItem key={blog.id}>
                          <SidebarMenuSubButton
                            asChild
                            isActive={location.pathname === `/blogs/${blog.id}`}
                          >
                            <Link to={`/blogs/${blog.id}`} title={blog.title}>
                              <span className="truncate">{blog.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))
                    )}
                  </>
                ) : (
                  item.items!.map(subItem => (
                    <SidebarMenuSubItem key={subItem.title}>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === subItem.url}
                      >
                        <Link to={subItem.url}>
                          <span>{subItem.title}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))
                )}
              </SidebarMenuSub>
            </motion.div>
          )}
        </AnimatePresence>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={item.title} isActive={isActive}>
        {external ? (
          <a href={item.url} target="_blank" rel="noopener noreferrer">
            {item.icon && <item.icon />}
            <span>{item.title}</span>
          </a>
        ) : (
          <Link to={item.url}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
          </Link>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function NavMain({ items }: { items: NavItemProps[] }) {
  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map(item => (
            <NavMainItem key={item.title} item={item} />
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
